import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import { SeparatorVertical } from 'components/styles';
import Tabs, { TabData } from 'mantine-components/widget/tabs';
import React from 'react';
import UniskaiFormTemplateProvider from 'templates/hooks/use-uniskai-form-template-context';

import UniskaiForm, { UniskaiFormProps } from './uniskai-form';
import UniskaiFormFooter, {
  UniskaiFormFooterProps,
  UniskaiFormFooterRef,
} from './uniskai-form-footer';
import UniskaiFormHeader, {
  UniskaiFormHeaderProps,
  UniskaiFormHeaderRef,
} from './uniskai-form-header';

export interface UniskaiFormTemplateProps<T = any>
  extends UniskaiFormHeaderProps<T> {
  formProps: Omit<UniskaiFormProps, 'children'>;
  footerProps?: Omit<UniskaiFormFooterProps, 'saveMutation' | 'data'>;
  children: React.ReactNode;
  tabs?: TabData[];
  tabValue?: string;
  titleRef?: React.RefObject<UniskaiFormHeaderRef>;
  totalRef?: React.RefObject<UniskaiFormFooterRef>;
}

export default function UniskaiFormTemplate<T>(
  props: UniskaiFormTemplateProps<T>,
) {
  const {
    formProps,
    footerProps,
    // combined footer and header
    saveMutation,
    customs = [],
    query,
    children,
    type = 'default',
    //tabs
    tabs = [],
    tabValue,
    //header
    ...headerProps
  } = props;

  const queryData = React.useMemo(() => {
    return query?.data?.data;
  }, [query?.data?.data]);

  const queryError = React.useMemo(() => {
    return query?.error;
  }, [query?.error]);

  const hasTab = React.useMemo(() => {
    return tabs.length > 0;
  }, [tabs.length]);

  const cancelProps = React.useMemo(() => {
    return customs.find((item) => item.type === 'cancel');
  }, [customs]);

  const saveProps = React.useMemo(() => {
    return customs.find((item) => item.type === 'save');
  }, [customs]);

  const component = (
    <UniskaiFormTemplateProvider
      titleRef={props.titleRef}
      totalRef={props.totalRef}
    >
      {({ titleRef, totalRef }) => {
        return (
          <>
            <UniskaiForm {...formProps}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                  }}
                >
                  <UniskaiFormHeader
                    {...headerProps}
                    data={queryData}
                    customs={customs}
                    saveMutation={saveMutation}
                    query={query}
                    type={type}
                    ref={titleRef}
                  />
                  <SeparatorVertical $margin={8} />
                  {hasTab && <Tabs value={tabValue} data={tabs} />}
                </div>
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'scroll',
                  }}
                >
                  {children}
                </div>
                {(type === 'default-footer' || footerProps) && (
                  <UniskaiFormFooter
                    {...footerProps}
                    saveMutation={saveMutation}
                    data={queryData}
                    cancelProps={cancelProps}
                    saveProps={saveProps}
                    ref={totalRef}
                  />
                )}
              </div>
            </UniskaiForm>
          </>
        );
      }}
    </UniskaiFormTemplateProvider>
  );

  if (query) {
    return (
      <FetchWrapperComponent
        onRetry={query.refetch}
        isLoading={query.isLoading || query.isFetching}
        error={queryError}
        component={component}
      />
    );
  }

  return component;
}
